// Round-trip import of the DM matrix + Illustrations matrix from CSV / XLSX.
// Accepts the same layout the app exports (see exporters.js → exportCsv / exportXlsx).
import * as XLSX from "xlsx";
import { DM_TYPES, ILLUSTRATION_LEVELS, LEVELS, buildEmptyDmMatrix, buildEmptyIllustrations } from "./s1000dData";

const DM_HEADER_KEYS = ["code", "type", "simpleQty", "simpleHrs", "_sTot", "intQty", "intHrs", "_iTot", "complexQty", "complexHrs", "_cTot", "_typeTot"];
const ILLUS_HEADER_KEYS = ["level", "qty", "hours", "_totalHrs"];

const DM_BLOCK = "DATA MODULES MATRIX";
const ILLUS_BLOCK = "ILLUSTRATIONS";

function toNum(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/[, ]+/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function normaliseHeader(s) {
  return String(s || "").trim().toLowerCase();
}

/**
 * Parse a CSV/XLSX file (File object) and return a normalised dump of cells
 * as an array of rows (each row an array of strings).
 */
async function readFileAsRows(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  // raw:false ensures cells come back as formatted strings — critical for DM type codes
  // like "040", "057", "941" which would otherwise be coerced to numbers and lose their
  // leading zero.
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false });
}

/**
 * Find a block header row index by matching first non-empty cell, case-insensitive.
 */
function findBlockStart(rows, blockName) {
  for (let i = 0; i < rows.length; i++) {
    const first = normaliseHeader(rows[i]?.[0]);
    if (first === blockName.toLowerCase()) return i;
  }
  return -1;
}

/**
 * Slice the rows that belong to a block — from the header row+2 (skip column header)
 * until either an empty row or the next ALL CAPS block name.
 */
function sliceBlockRows(rows, startIndex) {
  const out = [];
  for (let i = startIndex + 2; i < rows.length; i++) {
    const row = rows[i] || [];
    const first = String(row[0] ?? "").trim();
    if (!first) break;
    // If first cell is all caps and matches a known block name pattern, stop
    if (/^[A-Z &/-]+$/.test(first) && row.filter((c) => String(c ?? "").trim() !== "").length <= 2) break;
    out.push(row);
  }
  return out;
}

/**
 * Parse DM matrix block.
 */
function parseDmBlock(rows) {
  const start = findBlockStart(rows, DM_BLOCK);
  if (start === -1) return { matrix: null, warnings: ["No 'DATA MODULES MATRIX' block found"], rowCount: 0 };
  const dataRows = sliceBlockRows(rows, start);
  const matrix = buildEmptyDmMatrix();
  const warnings = [];
  let usedCount = 0;
  const knownCodes = new Set(DM_TYPES.map((t) => t.code.toUpperCase()));
  for (const r of dataRows) {
    const code = String(r[0] ?? "").trim().toUpperCase();
    if (!code) continue;
    if (!knownCodes.has(code)) {
      warnings.push(`Unknown DM type code skipped: "${code}"`);
      continue;
    }
    const cells = {
      simple:       { qty: toNum(r[2]), hours: toNum(r[3]) },
      intermediate: { qty: toNum(r[5]), hours: toNum(r[6]) },
      complex:      { qty: toNum(r[8]), hours: toNum(r[9]) },
    };
    // Apply default hours when import sheet left blank but a qty exists
    for (const lvl of LEVELS) {
      if (cells[lvl.key].qty > 0 && cells[lvl.key].hours === 0) {
        cells[lvl.key].hours = lvl.defaultHours;
      }
    }
    matrix[code] = cells;
    usedCount++;
  }
  return { matrix, warnings, rowCount: usedCount };
}

/**
 * Parse Illustrations block.
 */
function parseIllusBlock(rows) {
  const start = findBlockStart(rows, ILLUS_BLOCK);
  if (start === -1) return { illustrations: null, warnings: ["No 'ILLUSTRATIONS' block found"], rowCount: 0 };
  const dataRows = sliceBlockRows(rows, start);
  const illustrations = buildEmptyIllustrations();
  const warnings = [];
  let usedCount = 0;
  const labelToKey = new Map(ILLUSTRATION_LEVELS.map((l) => [l.label.toLowerCase(), l.key]));
  for (const r of dataRows) {
    const lvlLabel = String(r[0] ?? "").trim().toLowerCase();
    if (!lvlLabel) continue;
    const key = labelToKey.get(lvlLabel) || ILLUSTRATION_LEVELS.find((l) => l.key.toLowerCase() === lvlLabel)?.key;
    if (!key) {
      warnings.push(`Unknown illustration level skipped: "${r[0]}"`);
      continue;
    }
    const lvl = ILLUSTRATION_LEVELS.find((l) => l.key === key);
    const qty = toNum(r[1]);
    let hours = toNum(r[2]);
    if (qty > 0 && hours === 0) hours = lvl.defaultHours;
    illustrations[key] = { qty, hours };
    usedCount++;
  }
  return { illustrations, warnings, rowCount: usedCount };
}

/**
 * Entry point — parses a CSV / XLSX File and returns whatever blocks were found.
 * `target` (optional): "dm" / "illus" / "both" — suppresses warnings for the block
 * that the caller isn't interested in.
 */
export async function parseMatrixImport(file, target = "both") {
  if (!file) throw new Error("No file selected");
  const ext = (file.name || "").toLowerCase().split(".").pop();
  if (!["csv", "xlsx", "xls"].includes(ext)) {
    throw new Error(`Unsupported file type: .${ext}. Use .csv or .xlsx`);
  }
  let rows;
  try {
    rows = await readFileAsRows(file);
  } catch (err) {
    throw new Error(`Failed to parse file: ${err.message || err}`);
  }
  if (!rows || rows.length === 0) throw new Error("File is empty");

  const dm = parseDmBlock(rows);
  const il = parseIllusBlock(rows);

  if (!dm.matrix && !il.illustrations) {
    throw new Error("No 'DATA MODULES MATRIX' or 'ILLUSTRATIONS' blocks found in the file");
  }

  // Suppress "block not found" warnings for blocks the caller didn't ask for.
  const dmWarnings = target === "illus"
    ? dm.warnings.filter((w) => !w.includes("DATA MODULES MATRIX"))
    : dm.warnings;
  const ilWarnings = target === "dm"
    ? il.warnings.filter((w) => !w.includes("ILLUSTRATIONS"))
    : il.warnings;

  return {
    dmMatrix: dm.matrix,
    illustrations: il.illustrations,
    dmRowCount: dm.rowCount,
    illusRowCount: il.rowCount,
    warnings: [...dmWarnings, ...ilWarnings],
  };
}

/**
 * Merge two DM matrices — newMatrix wins for any code that has at least one
 * non-zero qty across levels. Levels with qty=0 keep their existing values.
 */
export function mergeDmMatrix(existing, incoming) {
  const out = buildEmptyDmMatrix();
  for (const t of DM_TYPES) {
    const cur = existing[t.code] || {};
    const inc = incoming[t.code] || {};
    out[t.code] = {};
    for (const lvl of LEVELS) {
      const incCell = inc[lvl.key] || { qty: 0, hours: 0 };
      const curCell = cur[lvl.key] || { qty: 0, hours: lvl.defaultHours };
      // If incoming has any non-zero values, take it; else keep current
      if (incCell.qty > 0 || incCell.hours > 0) {
        out[t.code][lvl.key] = { qty: incCell.qty, hours: incCell.hours };
      } else {
        out[t.code][lvl.key] = { ...curCell };
      }
    }
  }
  return out;
}

/**
 * Merge two illustrations objects with the same rule as mergeDmMatrix.
 */
export function mergeIllustrations(existing, incoming) {
  const out = buildEmptyIllustrations();
  for (const lvl of ILLUSTRATION_LEVELS) {
    const inc = incoming[lvl.key] || {};
    const cur = existing[lvl.key] || { qty: 0, hours: lvl.defaultHours };
    if ((inc.qty || 0) > 0 || (inc.hours || 0) > 0) {
      out[lvl.key] = { qty: inc.qty || 0, hours: inc.hours || 0 };
    } else {
      out[lvl.key] = { ...cur };
    }
  }
  return out;
}

/**
 * Build a blank XLSX template — same columns as the export — so users can fill
 * in cells in Excel and import it back.
 */
export function buildImportTemplateXlsx() {
  const rows = [];
  rows.push(["S1000D Cost Estimator — Import Template"]);
  rows.push(["Filename hint: replace this row with your project name if you like."]);
  rows.push([]);
  rows.push([DM_BLOCK]);
  rows.push([
    "Code", "Type",
    "Simple Qty", "Simple Hrs/unit", "Simple Total Hrs",
    "Intermediate Qty", "Intermediate Hrs/unit", "Intermediate Total Hrs",
    "Complex Qty", "Complex Hrs/unit", "Complex Total Hrs",
    "Type Total Hrs",
  ]);
  for (const t of DM_TYPES) {
    rows.push([t.code, t.name, 0, LEVELS[0].defaultHours, 0, 0, LEVELS[1].defaultHours, 0, 0, LEVELS[2].defaultHours, 0, 0]);
  }
  rows.push([]);
  rows.push([ILLUS_BLOCK]);
  rows.push(["Level", "Qty", "Hrs/unit", "Total Hrs"]);
  for (const lvl of ILLUSTRATION_LEVELS) {
    rows.push([lvl.label, 0, lvl.defaultHours, 0]);
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Matrix");
  return wb;
}

export function downloadImportTemplate() {
  const wb = buildImportTemplateXlsx();
  XLSX.writeFile(wb, "s1000d_matrix_template.xlsx");
}

// S1000D Bike sample — realistic DM mix for the well-known Bike data set.
// Quantities reflect a small-to-medium bicycle publication (about 90 DMs)
// across the most common DM types, with hours-per-unit set to the indicative
// 6 / 8 / 12 figures so users can see a complete cost roll-up immediately.
const BIKE_SAMPLE_QTYS = {
  // code: [simple, intermediate, complex]
  "040": [8, 5, 2],   // Descriptive
  "022": [1, 1, 1],   // BREX
  "211": [0, 0, 0],   // Crew/Operator (no separate crew on a bike)
  "232": [2, 0, 0],   // Operator (rider quick-start)
  "250": [0, 0, 0],   // Wiring (none on a basic bike)
  "420": [4, 3, 1],   // Fault isolation
  "520": [10, 8, 3],  // Procedural (the bulk of a maintenance manual)
  "720": [6, 4, 1],   // Maintenance planning
  "941": [4, 3, 1],   // Illustrated parts data
  "945": [1, 0, 0],   // Service bulletin
};

export function buildBikeSampleMatrixXlsx() {
  const rows = [];
  rows.push(["S1000D Bike — Sample DM Matrix"]);
  rows.push(["Source: indicative DM mix for the publicly-known S1000D Bike training data set."]);
  rows.push([]);
  rows.push([DM_BLOCK]);
  rows.push([
    "Code", "Type",
    "Simple Qty", "Simple Hrs/unit", "Simple Total Hrs",
    "Intermediate Qty", "Intermediate Hrs/unit", "Intermediate Total Hrs",
    "Complex Qty", "Complex Hrs/unit", "Complex Total Hrs",
    "Type Total Hrs",
  ]);
  for (const t of DM_TYPES) {
    const q = BIKE_SAMPLE_QTYS[t.code] || [0, 0, 0];
    const hS = LEVELS[0].defaultHours, hI = LEVELS[1].defaultHours, hC = LEVELS[2].defaultHours;
    const tS = q[0] * hS, tI = q[1] * hI, tC = q[2] * hC;
    rows.push([t.code, t.name, q[0], hS, tS, q[1], hI, tI, q[2], hC, tC, tS + tI + tC]);
  }
  rows.push([]);
  rows.push([ILLUS_BLOCK]);
  rows.push(["Level", "Qty", "Hrs/unit", "Total Hrs"]);
  // Realistic illustration mix for a bike — most are simple line drawings
  // (exploded views of components), a handful of intermediate exploded views,
  // one or two complex cut-aways.
  const illusQty = [18, 8, 2];
  ILLUSTRATION_LEVELS.forEach((lvl, i) => {
    rows.push([lvl.label, illusQty[i], lvl.defaultHours, illusQty[i] * lvl.defaultHours]);
  });
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bike Matrix");
  return wb;
}

export function downloadBikeSampleMatrix() {
  XLSX.writeFile(buildBikeSampleMatrixXlsx(), "s1000d_bike_sample_matrix.xlsx");
}

// Keys exported for tests
export const __test = { DM_HEADER_KEYS, ILLUS_HEADER_KEYS, findBlockStart, sliceBlockRows };
